import React from 'react';
import { PageHeader, Badge, Stat } from './shared';
import { ModalHeader, ModalShell } from './Modals';
import { PipelineArrow, PipelineStage } from './Normalizers';
import { Icon } from './ui/icons';

function timeAgo(timestamp: string): string {
  const now = new Date();
  const eventTime = new Date(timestamp);
  const diffMs = now.getTime() - eventTime.getTime();
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);
  
  if (diffHours < 1) return 'Just now';
  if (diffHours < 24) return `${diffHours}h ago`;
  return `${diffDays}d ago`;
}

// Types
interface Control {
  id: string;
  name: string;
}

interface Schema {
  id: string;
  name: string;
  color: string;
}

interface Normalizer {
  id: string;
  analyzer: string;
  output: string;
  status: 'active' | 'draft' | 'changed' | 'paused';
  version: string;
}

interface Interpreter {
  id: string;
  name: string;
  description: string;
  schema: string;
  control: string;
  version: string;
  status: 'active' | 'draft' | 'changed' | 'paused';
  runs: number;
  failures: number;
  lastRun?: string;
  language: string;
  expression: string;
  owner: string;
}

interface InterpretersData {
  interpreters?: {
    interpreters: Interpreter[];
  };
  normalizers?: {
    schemas: Schema[];
    normalizers: Normalizer[];
  };
  controls: {
    controls: Control[];
  };
}

interface InterpretersProps {
  data: InterpretersData;
  navigate: (path: string) => void;
}

// Constants
const INT_STATUS_TONE = {
  active: 'ok',
  draft: 'draft',
  changed: 'warn',
  paused: 'muted',
} as const;

// Sub-components for SRP

interface InterpreterRowProps {
  interpreter: Interpreter;
  schemas: Schema[];
  controls: Control[];
  normalizers: Normalizer[];
  onOpen: (interpreter: Interpreter) => void;
}

function InterpreterRow({ interpreter, schemas, controls, normalizers, onOpen }: InterpreterRowProps) {
  const ctrl = controls.find((c) => c.id === interpreter.control);
  const schema = schemas.find((s) => s.id === interpreter.schema);
  const sources = normalizers.filter((n) => n.output === interpreter.schema);
  const passRate = interpreter.runs ? Math.round(((interpreter.runs - interpreter.failures) / interpreter.runs) * 100) : null;
  
  return (
    <li
      onClick={() => onOpen(interpreter)}
      className="grid items-center px-5 py-4 border-b border-[#F7EDDB] last:border-0 hover:bg-[#FCF8F1] cursor-pointer"
      style={{ gridTemplateColumns: '1.5fr 1.2fr 1.1fr 0.9fr 1fr 90px' }}
      onKeyDown={(e) => e.key === 'Enter' && e.currentTarget.click()}
    >
      <div className="min-w-0">
        <div className="font-display font-bold text-ink text-[14px] truncate">{interpreter.name}</div>
        <div className="font-data text-[11px] text-ink/45 mt-0.5">
          #{interpreter.id} · {interpreter.version}
        </div>
      </div>
      <span className="inline-flex items-center gap-2 font-display text-[13px]">
        <span
          className="h-2.5 w-2.5 rounded-full shrink-0"
          style={{ background: schema?.color || '#999' }}
        />
        <span className="font-bold text-ink">{interpreter.schema}</span>
        <span className="font-data text-[11px] text-ink/55 truncate">
          ({sources.length} source{sources.length !== 1 ? 's' : ''})
        </span>
      </span>
      <span className="inline-flex items-center gap-2">
        <span className="font-data text-[11px] px-1.5 py-[2px] rounded-[3px] bg-[#EDE5FC] text-[#7F56D9]">
          #{interpreter.control}
        </span>
        <span className="font-display text-[13px] text-ink/80 truncate">{ctrl?.name || ''}</span>
      </span>
      <span>
        {passRate == null ? (
          <span className="font-data text-[12px] text-ink/40">No runs</span>
        ) : (
          <div className="flex items-center gap-2 max-w-[140px]">
            <div className="flex-1 h-1.5 rounded-full bg-[#FDD3D3] overflow-hidden">
              <div className="h-full rounded-full bg-[#159D4C]" style={{ width: `${passRate}%` }} />
            </div>
            <span className="font-display font-bold text-ink text-[12px] w-9 text-right">
              {passRate}%
            </span>
          </div>
        )}
      </span>
      <span className="font-data text-[12px] text-ink/55">
        {interpreter.lastRun ? timeAgo(interpreter.lastRun) : <span className="text-ink/30">—</span>}
      </span>
      <Badge tone={INT_STATUS_TONE[interpreter.status] || 'muted'} dot>
        {interpreter.status}
      </Badge>
    </li>
  );
}

interface InterpreterPanelProps {
  open: boolean;
  interpreter: Interpreter | null;
  schemas: Schema[];
  controls: Control[];
  normalizers: Normalizer[];
  onClose: () => void;
  navigate?: (path: string) => void;
}

function InterpreterPanel({ open, interpreter, schemas, controls, normalizers, onClose, navigate }: InterpreterPanelProps) {
  if (!interpreter) return null;
  
  const ctrl = controls.find((c) => c.id === interpreter.control);
  const schema = schemas.find((s) => s.id === interpreter.schema);
  const sources = normalizers.filter((n) => n.output === interpreter.schema);
  
  return (
    <ModalShell open={open} onClose={onClose} width={900}>
      <ModalHeader
        title={interpreter.name}
        subtitle={`#${interpreter.id} · ${interpreter.version}`}
        onClose={onClose}
        icon="git-branch"
      />
      <div className="px-6 py-5 flex flex-col gap-5 overflow-y-auto">
        <p className="font-display text-[14px] text-ink/75">{interpreter.description}</p>

        {/* 4-stage pipeline */}
        <div className="rounded-[10px] border border-[#F3E4C8] bg-[#FCF8F1] p-4">
          <div className="font-display text-[11px] font-bold uppercase tracking-[0.04em] text-ink/55 mb-3">
            Evidence pipeline
          </div>
          <div
            className="grid items-center gap-1"
            style={{
              gridTemplateColumns:
                'minmax(0,1fr) 28px minmax(0,1fr) 28px minmax(0,1fr) 28px minmax(0,1fr)',
            }}
          >
            <PipelineStage
              title={sources.length ? sources.map((s) => s.analyzer).join(', ') : 'Any analyzer'}
              sub={`${sources.length} source${sources.length !== 1 ? 's' : ''}`}
              icon="package"
            />
            <PipelineArrow label="normalize" />
            <PipelineStage
              title={schema?.name || interpreter.schema}
              sub={`schema ${interpreter.schema}`}
              icon="shield"
              accent={schema?.color}
            />
            <PipelineArrow label="interpret" />
            <PipelineStage title={interpreter.id} sub="this interpreter" icon="git-branch" />
            <PipelineArrow label="verdict" />
            <PipelineStage
              title={`#${interpreter.control}`}
              sub={ctrl?.name || ''}
              icon="check"
              accent="#FA500E"
            />
          </div>
        </div>

        {/* Expression (now schema-aware) */}
        <div>
          <div className="font-display font-bold text-ink text-[14px] mb-2">Rule expression</div>
          <pre className="rounded-[8px] border border-[#F3E4C8] bg-[#121111] text-[#F7EDDB] p-4 font-data text-[12.5px] leading-[1.6] overflow-x-auto">
            <span className="text-[#9CA3AF]">
              {/* */}
              {interpreter.language} · operates on canonical {interpreter.schema}[]
            </span>
            {'\n'}
            <span className="text-[#F9C12E]">{interpreter.control}</span>.pass = ({'\n  '}
            {interpreter.expression}
            {'\n'})
          </pre>
        </div>

        {/* Sources detail */}
        {sources.length > 0 && (
          <SourcesList
            sources={sources}
            schema={interpreter.schema}
            onClose={onClose}
            navigate={navigate}
          />
        )}

        <div className="grid grid-cols-3 gap-4">
          <Stat label="Runs" value={interpreter.runs.toLocaleString()} />
          <Stat
            label="Failures"
            value={interpreter.failures.toLocaleString()}
            tone={interpreter.failures ? 'crit' : 'muted'}
          />
          <Stat
            label="Pass rate"
            value={interpreter.runs ? `${Math.round(((interpreter.runs - interpreter.failures) / interpreter.runs) * 100)}%` : '—'}
            tone="ok"
          />
        </div>
      </div>
      <div className="px-6 py-4 border-t border-[#F7EDDB] flex justify-between items-center">
        <span className="font-data text-[12px] text-ink/55">Owned by {interpreter.owner}</span>
        <div className="flex gap-2">
          <button
            type="button"
            onClick={onClose}
            className="h-10 px-4 rounded-[8px] border border-[#F3E4C8] font-display font-semibold text-[14px] hover:bg-[#FCF8F1]"
          >
            Close
          </button>
          <button
            type="button"
            onClick={() =>
              (window as any).notify?.('Expression editor', {
                sub: 'Opening the schema-aware editor…',
                tone: 'info',
              })
            }
            className="h-10 px-4 rounded-[8px] bg-[#FA500E] text-white font-display font-bold text-[14px] hover:opacity-90 whitespace-nowrap"
          >
            Edit expression
          </button>
        </div>
      </div>
    </ModalShell>
  );
}

interface SourcesListProps {
  sources: Normalizer[];
  schema: string;
  onClose: () => void;
  navigate?: (path: string) => void;
}

function SourcesList({ sources, schema, onClose, navigate }: SourcesListProps) {
  return (
    <div>
      <div className="font-display font-bold text-ink text-[14px] mb-2">
        Where the {schema} comes from
      </div>
      <ul className="grid grid-cols-2 gap-2">
        {sources.map((n) => (
          <li key={n.id}>
            <button
              type="button"
              onClick={() => {
                onClose();
                navigate?.('normalizers');
              }}
              className="w-full flex items-center gap-3 rounded-[8px] border border-[#F3E4C8] bg-[#FCF8F1] px-3 py-2.5 text-left hover:border-ink/40"
            >
              <span className="h-8 w-8 rounded-[8px] grid place-items-center bg-white border border-[#F3E4C8]">
                <Icon name="package" size={13} stroke="#121111" />
              </span>
              <div className="flex-1 min-w-0">
                <div className="font-display font-bold text-ink text-[13px] truncate">
                  {n.analyzer}
                </div>
                <div className="font-data text-[11px] text-ink/55 mt-0.5 truncate">
                  {n.id} · {n.version}
                </div>
              </div>
              <Badge tone={n.status === 'active' ? 'ok' : 'warn'} dot>
                {n.status}
              </Badge>
            </button>
          </li>
        ))}
      </ul>
    </div>
  );
}

interface FilterBarProps {
  query: string;
  onQueryChange: (query: string) => void;
}

function FilterBar({ query, onQueryChange }: FilterBarProps) {
  return (
    <div className="flex items-center justify-between gap-3 rounded-[4px] border border-[rgba(18,17,17,0.2)] px-5 h-12">
      <div className="flex items-center gap-3 flex-1">
        <Icon name="search" size={18} stroke="#121111" />
        <input
          value={query}
          onChange={(e) => onQueryChange(e.target.value)}
          placeholder="Find an interpreter…"
          className="flex-1 bg-transparent outline-none font-display text-[14px] font-medium placeholder:text-ink/45"
        />
      </div>
      {query && (
        <button type="button" onClick={() => onQueryChange('')}>
          <Icon name="x" size={18} stroke="#121111" />
        </button>
      )}
    </div>
  );
}

interface SchemaFiltersProps {
  schemas: Schema[];
  selectedFilter: string;
  onFilterChange: (filter: string) => void;
  totalItems: number;
}

function SchemaFilters({ schemas, selectedFilter, onFilterChange, totalItems }: SchemaFiltersProps) {
  return (
    <div className="flex items-center gap-1 flex-wrap">
      <button
        type="button"
        onClick={() => onFilterChange('all')}
        className={cx(
          'px-3 h-9 rounded-[8px] font-display text-[13px] inline-flex items-center gap-2',
          selectedFilter === 'all'
            ? 'bg-ink text-white font-semibold'
            : 'text-ink/70 hover:bg-[#F7EDDB]'
        )}
      >
        All{' '}
        <span className="font-data text-[11px] rounded-full px-1.5 min-w-[20px] text-center bg-white/15">
          {totalItems}
        </span>
      </button>
      {schemas.map((s) => (
        <button
          type="button"
          key={s.id}
          onClick={() => onFilterChange(s.id)}
          className={cx(
            'px-3 h-9 rounded-[8px] font-display text-[13px] inline-flex items-center gap-2',
            selectedFilter === s.id
              ? 'bg-ink text-white font-semibold'
              : 'text-ink/70 hover:bg-[#F7EDDB]'
          )}
        >
          <span className="h-2 w-2 rounded-full" style={{ background: s.color }} />
          {s.id}
        </button>
      ))}
    </div>
  );
}

interface InterpretersTableProps {
  interpreters: Interpreter[];
  schemas: Schema[];
  controls: Control[];
  normalizers: Normalizer[];
  onOpenInterpreter: (interpreter: Interpreter) => void;
}

function InterpretersTable({ interpreters, schemas, controls, normalizers, onOpenInterpreter }: InterpretersTableProps) {
  return (
    <div className="rounded-[6px] border border-[#F3E4C8] bg-white overflow-hidden">
      <div
        className="grid items-center px-5 h-[54px] border-b border-[#F7EDDB] bg-[#FCF8F1] font-display font-bold text-[13px] text-ink"
        style={{ gridTemplateColumns: '1.5fr 1.2fr 1.1fr 0.9fr 1fr 90px' }}
      >
        <span>Interpreter</span>
        <span>Reads schema</span>
        <span>Feeds control</span>
        <span>Pass rate</span>
        <span>Last run</span>
        <span>Status</span>
      </div>
      <ul>
        {interpreters.map((it) => (
          <InterpreterRow
            key={it.id}
            interpreter={it}
            schemas={schemas}
            controls={controls}
            normalizers={normalizers}
            onOpen={onOpenInterpreter}
          />
        ))}
      </ul>
    </div>
  );
}

// Main component
export default function Interpreters({ data, navigate }: InterpretersProps) {
  const [q, setQ] = React.useState('');
  const [schemaFilter, setSchemaFilter] = React.useState('all');
  const [opened, setOpened] = React.useState<Interpreter | null>(null);
  
  const items = data.interpreters?.interpreters || [];
  const schemas = data.normalizers?.schemas || [];
  const normalizers = data.normalizers?.normalizers || [];

  const list = items.filter((it) => {
    if (schemaFilter !== 'all' && it.schema !== schemaFilter) return false;
    if (!q.trim()) return true;
    const hay = (it.name + it.id + it.control + it.schema).toLowerCase();
    return hay.includes(q.toLowerCase());
  });

  const avgPass = React.useMemo(() => {
    const withRuns = items.filter((i) => i.runs);
    if (!withRuns.length) return '—';
    const m = withRuns.reduce((a, i) => a + (i.runs - i.failures) / i.runs, 0) / withRuns.length;
    return `${Math.round(m * 100)}%`;
  }, [items]);

  return (
    <div className="flex flex-col gap-8">
      <PageHeader
        title="Interpreters"
        subtitle="Schema-aware rules that evaluate canonical evidence and emit a control verdict. Independent of any specific analyzer."
        right={
          <div className="flex items-center gap-2 shrink-0">
            <button
              type="button"
              onClick={() => navigate('normalizers')}
              className="h-10 px-4 rounded-[10px] border border-[#F3E4C8] bg-white font-display font-bold text-[14px] inline-flex items-center gap-2 hover:border-ink/40"
            >
              <Icon name="package" size={14} stroke="#121111" /> Manage normalizers
            </button>
            <button
              type="button"
              onClick={() => (window as any).openCreate?.('interpreter')}
              className="h-10 px-4 rounded-[10px] bg-[#FA500E] text-white font-display font-bold text-[15px] inline-flex items-center gap-2 hover:opacity-90 whitespace-nowrap"
            >
              <Icon name="plus" size={16} stroke="#FFF" /> New interpreter
            </button>
          </div>
        }
      />

      <div className="grid grid-cols-4 gap-4">
        <Stat label="Interpreters" value={items.length} />
        <Stat label="Active" value={items.filter((i) => i.status === 'active').length} tone="ok" />
        <Stat
          label="Draft / changed"
          value={items.filter((i) => i.status === 'draft' || i.status === 'changed').length}
          tone="warn"
        />
        <Stat label="Avg pass rate" value={avgPass} tone="ok" />
      </div>

      <FilterBar query={q} onQueryChange={setQ} />

      <SchemaFilters
        schemas={schemas}
        selectedFilter={schemaFilter}
        onFilterChange={setSchemaFilter}
        totalItems={items.length}
      />

      <InterpretersTable
        interpreters={list}
        schemas={schemas}
        controls={data.controls.controls}
        normalizers={normalizers}
        onOpenInterpreter={setOpened}
      />

      <InterpreterPanel
        open={!!opened}
        interpreter={opened}
        schemas={schemas}
        controls={data.controls.controls}
        normalizers={normalizers}
        onClose={() => setOpened(null)}
        navigate={navigate}
      />
    </div>
  );
}